export type SyncRetryPolicy = {
  maxAttempts: number;
  baseDelayMs: number;
  maxDelayMs: number;
};

export const DEFAULT_SYNC_RETRY_POLICY: SyncRetryPolicy = {
  maxAttempts: 3,
  baseDelayMs: 250,
  maxDelayMs: 4_000,
};

export function isRetryableSyncStatus(status: number): boolean {
  return status === 408 || status === 425 || status === 429 || status >= 500;
}

export function syncRetryDelayMs(attempt: number, policy: SyncRetryPolicy = DEFAULT_SYNC_RETRY_POLICY): number {
  const safeAttempt = Math.max(0, Math.floor(attempt));
  return Math.min(policy.maxDelayMs, policy.baseDelayMs * (2 ** safeAttempt));
}

export async function withSyncRetry<T>(
  operation: (attempt: number) => Promise<T>,
  shouldRetry: (error: unknown) => boolean,
  policy: SyncRetryPolicy = DEFAULT_SYNC_RETRY_POLICY,
  sleep: (delayMs: number) => Promise<void> = (delayMs) => new Promise((resolve) => setTimeout(resolve, delayMs)),
): Promise<T> {
  const maxAttempts = Math.max(1, Math.floor(policy.maxAttempts));
  let attempt = 0;
  while (true) {
    try {
      return await operation(attempt);
    } catch (error) {
      attempt += 1;
      if (attempt >= maxAttempts || !shouldRetry(error)) throw error;
      await sleep(syncRetryDelayMs(attempt - 1, policy));
    }
  }
}
